import React, { useState, useEffect, useCallback } from 'react';

import styled from 'styled-components';
import cloneDeep from 'lodash/cloneDeep';
import InfiniteScroll from 'react-infinite-scroller';
import { Glyphicon } from 'react-bootstrap';
import { AxiosError } from 'axios';

import * as Service from '../services/RnrsService';
import * as ActivitiesService from '../services/ActivitiesService';
import TopPanel from '../components/utils/TopPanel';
import RnrsList from '../components/rnrs/RnrsList';
import TableSortingElem from '../infrastructure/TableSorting';
import { sortArray } from '../infrastructure/utils/common';
import MobileListSorter from '../components/utils/MobileListSorter';
import MobileTopPanelActions from '../components/utils/MobileTopPanelActions';
import ReviewResponseModal from '../components/rnrs/ReviewResponseModal';
import RnrsFilterForm from '../components/forms/RnrsFilterForm';
import RnrsBulkUploadPreview, {
  RnrsData,
} from '../components/rnrs/RnrsBulkUploadPreview';

const StyledWrapper = styled.div`
  h1 {
    margin-top: 0;
  }

  @media (max-width: 768px) {
    &&& {
      h1 {
        padding-left: 30px;
      }
    }
  }
`;

const StyledLoaderWrapper = styled.div`
  text-align: center;
  position: absolute;
  left: 0;
  right: 0;
  top: 30%;
  margin: auto;
`;

const availableSort = [
  new TableSortingElem({
    name: 'productId',
    up: true,
    label: 'Product ID',
    isNumber: true,
  }),
  new TableSortingElem({
    name: 'productId',
    up: false,
    label: 'Product ID',
    isNumber: true,
  }),
  new TableSortingElem({
    name: 'rating',
    up: true,
    label: 'Rating',
    isNumber: true,
  }),
  new TableSortingElem({
    name: 'rating',
    up: false,
    label: 'Rating',
    isNumber: true,
  }),
  new TableSortingElem({
    name: 'review',
    up: true,
    label: 'Review',
  }),
  new TableSortingElem({
    name: 'review',
    up: false,
    label: 'Review',
  }),
  new TableSortingElem({
    name: 'vendorReview',
    up: true,
    label: 'Vendor Review',
  }),
  new TableSortingElem({
    name: 'vendorReview',
    up: false,
    label: 'Vendor Review',
  }),
  new TableSortingElem({
    name: 'createdAt',
    up: true,
    label: 'Date Submitted',
  }),
  new TableSortingElem({
    name: 'createdAt',
    up: false,
    label: 'Date Submitted',
  }),
];

const Rnrs = () => {
  const [rnrs, setRnrs] = useState([]);
  const [lastEvaluatedKeyData, setLastEvaluatedKeyData] = useState();
  const [reviewToResponse, setReviewToResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [infinityScrollLoading, setInfinityScrollLoading] = useState(false);
  const [activeSort, setActiveSort] = useState(availableSort[0]);
  const [filterSelections, setFilterSelections] = useState(null);
  const [cities, setCities] = useState([]);
  const [mobileActions, setMobileActions] = useState({
    isMobileSortSelected: false,
    isMobileFilterSelected: false,
  });

  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [bulkUploadState, setBulkUploadState] = useState({});

  const fetchData = async () => {
    try {
      setInfinityScrollLoading(true);
      if (!lastEvaluatedKeyData) setLoading(true);
      const { reviews, lastEvaluatedKey } = await Service.getAllRnrs(
        lastEvaluatedKeyData
      );
      setLastEvaluatedKeyData(lastEvaluatedKey);
      setRnrs(sortArray([...rnrs, ...reviews], activeSort.name, activeSort.up));
      const fetchedCities = await ActivitiesService.getCities();
      setCities(fetchedCities);
    } catch (e) {
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
      console.error(e);
    } finally {
      setLoading(false);
      setInfinityScrollLoading(false);
    }
  };

  const changeSort = name => {
    const sort = availableSort.find(s => s.name === name);
    if (!sort) return;
    if (activeSort && activeSort.name === name) {
      sort.up = !activeSort.up;
    }
    const sortedData = sortArray(rnrs, sort.name, sort.up);
    setActiveSort(sort);
    setRnrs(sortedData);
  };

  const changeSortBySelect = (name, up) => {
    const sort = availableSort.find(s => s.name === name && s.up === up);
    if (!sort) return;
    const sorted = sortArray(rnrs, name, up);
    setActiveSort(sort);
    setRnrs(sorted);
  };

  function reviewAction(val, resp, review) {
    const serviceCallName = Service[val];
    return serviceCallName(review.productId, review.userId, resp);
  }

  const setRnrsWithModified = modified => {
    const copy = cloneDeep(rnrs);
    const index = copy.findIndex(
      c => c.productId === modified.productId && c.userId === modified.userId
    );
    if (index === -1) return;
    copy[index] = modified;
    setRnrs(copy);
  };

  const onResponseSend = async (response, reviewToResponse) => {
    try {
      setLoading(true);
      setReviewToResponse(null);
      let modified;
      if (reviewToResponse.adminResponse) {
        modified = await reviewAction(
          'updateReviewResponse',
          response,
          reviewToResponse
        );
      } else {
        modified = await reviewAction(
          'createReviewResponse',
          response,
          reviewToResponse
        );
      }
      setRnrsWithModified(modified);
    } catch (e) {
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const onResponseDelete = async reviewToResponse => {
    try {
      setLoading(true);
      setReviewToResponse(null);
      const modified = await reviewAction(
        'deleteReviewResponse',
        undefined,
        reviewToResponse
      );
      setRnrsWithModified(modified);
    } catch (e) {
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const changeReviewVisibility = async review => {
    try {
      setLoading(true);
      const hidden = !review.hidden;
      const { productId, userId } = review;
      const modified = await Service.changeReviewVisibility(
        productId,
        userId,
        hidden
      );
      setRnrsWithModified({ ...review, ...modified });
    } catch (e) {
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredReviews = filterSelections =>
    setFilterSelections(filterSelections);

  const removeFilter = () => setFilterSelections(null);

  const filteredCollected = useCallback(() => {
    const collectedTrueKeys = {
      productId: '',
      partyLocation: '',
      rating: '',
    };

    if (filterSelections) {
      Object.entries(filterSelections)?.forEach(([key, value]) => {
        if (value) {
          collectedTrueKeys[key] = `${value}`;
        }
      });
    }
    return collectedTrueKeys;
  }, [JSON.stringify(filterSelections)]);

  const filterHandler = useCallback(
    filters => {
      const filterKeys = Object.keys(filters);
      return rnrs.filter(product =>
        filterKeys.every(key => {
          if (!filters[key]?.length) return true;
          if (key === 'productId') {
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            return product[key].match(new RegExp(filters[key], 'i'));
          }
          return filters[key]?.includes(product[key]);
        })
      );
    },
    [rnrs]
  );

  const searchProducts = useCallback(() => filterHandler(filteredCollected()), [
    filterHandler,
    filteredCollected,
  ]);

  const onBulkUploadSend = async (data: RnrsData) => {
    setLoading(true);
    setBulkUploadState({});

    try {
      await Service.batchCreateRnrs(data);

      setIsBulkUploadModalOpen(false);

      fetchData();
    } catch (e) {
      console.error(e);

      const error = e as AxiosError;
      if (error) {
        setBulkUploadState({ error: error.response?.data });
      } else {
        setBulkUploadState({ error: (e as Error).message });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <StyledWrapper>
      {reviewToResponse && (
        <ReviewResponseModal
          isOpen={reviewToResponse}
          closeModal={() => setReviewToResponse(null)}
          onSave={onResponseSend}
          reviewToResponse={reviewToResponse}
          onResponseDelete={onResponseDelete}
        />
      )}

      {isBulkUploadModalOpen && (
        <RnrsBulkUploadPreview
          isOpen={isBulkUploadModalOpen}
          state={bulkUploadState}
          closeModal={() => setIsBulkUploadModalOpen(false)}
          onSend={data => onBulkUploadSend(data)}
        />
      )}

      <TopPanel>
        <h1 className='col-md-6 col-sm-4 col-xs-3'>
          <span className='fas fa-beer' /> Ratings & Reviews
        </h1>

        <MobileTopPanelActions
          isFilterSelected={mobileActions.isMobileFilterSelected}
          isSortSelected={mobileActions.isMobileSortSelected}
          onSortClick={() =>
            setMobileActions({
              ...mobileActions,
              isMobileSortSelected: !mobileActions.isMobileSortSelected,
            })
          }
          onFilterClick={() =>
            setMobileActions({
              ...mobileActions,
              isMobileFilterSelected: !mobileActions.isMobileFilterSelected,
            })
          }
          noFilter={false}
          noSorter={false}
        />

        <div className='page-actions'>
          <button
            type='button'
            onClick={() => setIsBulkUploadModalOpen(true)}
            className='btn btn-primary pull-right'
          >
            Bulk Upload
          </button>
        </div>
      </TopPanel>
      <div className='data-container'>
        <RnrsFilterForm
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          filteredReviews={getFilteredReviews}
          cities={cities}
          filterData={filterSelections}
          clear={removeFilter}
          isMobileFilterSelected={mobileActions.isMobileFilterSelected}
        />
        <MobileListSorter
          isSortSelected={mobileActions.isMobileSortSelected}
          activeSort={activeSort}
          availableSort={availableSort}
          changeSortBySelect={changeSortBySelect}
        />
        {loading && (
          <StyledLoaderWrapper>
            <Glyphicon key={0} glyph='refresh' className='spinning' />
          </StyledLoaderWrapper>
        )}
        <div className='clearfix' />
        {rnrs.length > 0 && (
          <div className='table-box tab-cont'>
            <InfiniteScroll
              pageStart={0}
              loadMore={fetchData}
              useWindow={false}
              hasMore={
                // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
                // @ts-ignore
                !infinityScrollLoading && !!lastEvaluatedKeyData?.productId
              }
              loader={
                <Glyphicon key={0} glyph='refresh' className='spinning' />
              }
            >
              <RnrsList
                rnrs={searchProducts()}
                activeSort={activeSort}
                changeSort={changeSort}
                setReviewToResponse={setReviewToResponse}
                changeReviewVisibility={changeReviewVisibility}
              />
            </InfiniteScroll>
          </div>
        )}
      </div>
    </StyledWrapper>
  );
};

export default Rnrs;
